import { COMPLETION_METHOD } from "#src/constants/completion-methods.ts";
import { Get, Post, Put } from "#src/HttpClient.ts";
import { type EappCreateDTO } from "#src/types/ElectronicApplication.ts";
import { parseErrorMessage } from "#src/util/helpers.ts";
import { type Pinia } from "pinia";

const controller = "/strife/v1/electronic_applications";

export async function eAppCreate(
  pinia: Pinia,
  electronic_application: Partial<EappCreateDTO>
) {
  try {
    const { data } = await Post(pinia, {
      url: controller,
      body: { electronic_application },
    });
    return data.electronic_application;
  } catch (e: any) {
    throw parseErrorMessage(e);
  }
}

export async function updateElectronicApplication(
  pinia: Pinia,
  id: number,
  electronic_application: any
) {
  const { data } = await Put(pinia, {
    url: `${controller}/${id}`,
    body: { electronic_application },
    includeEappData: true,
  });

  return data.electronic_application;
}

export async function examRequiredBackendCheck(pinia: Pinia, id: number) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/exam_required`,
    includeEappData: true,
  });
  return data.exam_required;
}

export async function eAppLoad(pinia: Pinia, uuid: string, body: any) {
  const { data } = await Get(pinia, {
    url: `${controller}/${uuid}`,
    params: body,
    includeEappData: true,
  });
  return data.electronic_application;
}

export async function eAppResume(
  pinia: Pinia,
  resume: string,
  id: number,
  uuid: string
) {
  const { data } = await Get(pinia, {
    url: `${controller}/${id}`,
    params: {
      electronic_application_id: id,
      uuid,
      resume,
    },
  });
  return data.electronic_application;
}

export async function sendEmail(
  pinia: Pinia,
  id: number,
  { to, message }: { to: string; message: string }
) {
  return Post(pinia, {
    url: `${controller}/${id}/send_email`,
    body: { to, message },
    includeEappData: true,
  });
}

export function notifyUnlicensedAgent(
  pinia: Pinia,
  id: number,
  not_licensed_in_state: string
) {
  return Post(pinia, {
    url: `${controller}/${id}/not_licensed_in_state`,
    body: { not_licensed_in_state },
    includeEappData: true,
  });
}

export async function getCarrierEAppUrl(pinia: Pinia, id: number) {
  const { data } = await Post(pinia, {
    url: `${controller}/${id}/resume_carrier_eapp`,
    includeEappData: true,
  });
  return data.url;
}

export async function getCompletionMethod(pinia: Pinia, id: number) {
  try {
    const { data } = await Post(pinia, {
      url: `${controller}/${id}/contracting`,
      includeEappData: true,
    });

    let submission_method = COMPLETION_METHOD.PAUSED_FOR_CONTRACTING;
    if (Object.values(COMPLETION_METHOD).includes(data.submission_method)) {
      submission_method = data.submission_method;
    }
    return {
      submission_method,
      carrier_eapp_url: data.carrier_eapp_url,
    };
  } catch (e: any) {
    throw {
      message: e.response?.data?.message,
      carrier_eapp_errors: e.response?.data?.carrier_eapp_errors,
    };
  }
}

export function submitEapp(pinia: Pinia, id: number, uuid: string) {
  return Post(pinia, {
    url: `${controller}/${id}/submit`,
    includeEappData: true,
    body: { websocket_uuid: uuid },
  });
}
